import {
  BookOpenIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { AgentRunCard } from '@/components/agents/AgentRunCard';
import { ButtonV2 } from '@/components/ui/ButtonV2';
import { buildAgentRunDisplayFromArtifact } from '@/lib/agentRunDisplay';
import { buildDecisionTraceFromArtifact, buildRunDetailsFromArtifact } from '@/lib/agentDecisionTrace';
import { shouldShowAgentDiagnostics } from '@/lib/agentDiagnostics';
import { approvedSourceCount, buildReadinessFromArtifact, formatApprovalBlocker } from '@/lib/contentArtifacts/approvalReadiness';
import { cn, sanitizeArtifactTitle, stripMarkdown } from '@/lib/utils';
import type { ArtifactContent } from '@/components/chat/ContentArtifact';
import { DecisionTracePanel, RunDetailsPanel } from './DecisionTracePanel';

const SHOW_AGENT_DIAGNOSTICS = shouldShowAgentDiagnostics();

type ArtifactGraphCompanionPanelProps = {
  artifact: ArtifactContent;
  onOpenHistory?: () => void;
  className?: string;
};

type CitationRow = {
  id: string;
  label: string;
  detail?: string;
  url?: string;
  status?: string;
};

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function shortId(value?: string): string {
  if (!value) return 'None';
  return value.length > 14 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function statusLabel(value?: string): string {
  return normalizeString(value).replace(/_/g, ' ') || 'Draft';
}

function statusTone(value?: string): string {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === 'approved' || normalized === 'ready') {
    return 'border-success/30 bg-success/10 text-success';
  }
  if (normalized === 'rejected' || normalized === 'blocked') {
    return 'border-destructive/30 bg-destructive/10 text-destructive';
  }
  if (normalized === 'needs_revision' || normalized === 'needs_attention') {
    return 'border-warning/30 bg-warning/10 text-warning';
  }
  return 'border-border/60 bg-muted/30 text-muted-foreground';
}

function isResolvedPolicyIssue(issue: Record<string, unknown>): boolean {
  const status = normalizeString(issue.status ?? issue.state ?? issue.resolution).toLowerCase();
  return status === 'resolved' || status === 'dismissed' || status === 'accepted';
}

function collectPolicyIssues(artifact: ArtifactContent): Array<Record<string, unknown>> {
  const issues: unknown[] = [
    ...(Array.isArray(artifact.policyIssues) ? artifact.policyIssues : []),
    ...(Array.isArray(artifact.format?.policy?.issues) ? artifact.format?.policy?.issues || [] : []),
  ];
  return issues.filter((issue): issue is Record<string, unknown> => Boolean(issue && typeof issue === 'object'));
}

function collectCitations(artifact: ArtifactContent): CitationRow[] {
  const rows: CitationRow[] = [];
  const seen = new Set<string>();

  const add = (row: CitationRow) => {
    const key = row.url || row.id || row.label;
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  };

  (artifact.format?.citations || []).forEach((citation, index) => {
    const label = normalizeString(citation.title)
      || normalizeString(citation.docId)
      || normalizeString(citation.url)
      || `Citation ${index + 1}`;
    add({
      id: normalizeString(citation.docId) || normalizeString(citation.url) || `format-citation-${index}`,
      label,
      detail: normalizeString(citation.claim) || normalizeString(citation.snippet) || normalizeString(citation.evidenceSnippet) || undefined,
      url: normalizeString(citation.url) || undefined,
      status: citation.grounded === false ? 'Needs support' : citation.grounded === true ? 'Grounded' : undefined,
    });
  });

  const citationPack = artifact.citationPackV1 || artifact.pressRelease?.citationPack || null;
  (citationPack?.sourceCards || []).forEach((source, index) => {
    const label = normalizeString(source.title)
      || normalizeString(source.url)
      || normalizeString(source.sourceId)
      || `Source ${index + 1}`;
    add({
      id: normalizeString(source.id) || normalizeString(source.sourceId) || normalizeString(source.url) || `source-card-${index}`,
      label,
      detail: normalizeString(source.evidenceSnippet) || normalizeString(source.snippet) || undefined,
      url: normalizeString(source.url) || undefined,
      status: statusLabel(source.approvedSourceStatus || source.status),
    });
  });

  (artifact.rag?.sources || []).forEach((source, index) => {
    const label = normalizeString(source.title)
      || normalizeString(source.url)
      || normalizeString(source.docId)
      || normalizeString(source.id)
      || `RAG source ${index + 1}`;
    add({
      id: normalizeString(source.id) || normalizeString(source.docId) || normalizeString(source.url) || `rag-source-${index}`,
      label,
      detail: normalizeString(source.text) || undefined,
      url: normalizeString(source.url) || undefined,
      status: typeof source.score === 'number' ? `${Math.round(source.score * 100)}% match` : undefined,
    });
  });

  return rows.slice(0, 5);
}

function Section({
  title,
  icon,
  children,
  className,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border border-border/60 bg-background px-3 py-3', className)}>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function MetricTile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className={cn('rounded-md border px-2.5 py-2', tone || 'border-border/60 bg-muted/20')}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-xs font-medium capitalize text-foreground">{value}</p>
    </div>
  );
}

export function ArtifactGraphCompanionPanel({
  artifact,
  onOpenHistory,
  className,
}: ArtifactGraphCompanionPanelProps) {
  const run = SHOW_AGENT_DIAGNOSTICS ? buildAgentRunDisplayFromArtifact(artifact) : null;
  const trace = SHOW_AGENT_DIAGNOSTICS ? buildDecisionTraceFromArtifact(artifact) : null;
  const details = SHOW_AGENT_DIAGNOSTICS ? buildRunDetailsFromArtifact(artifact) : null;
  const draftPreview = stripMarkdown(artifact.content || '').trim();
  const citations = collectCitations(artifact);
  const policyIssues = collectPolicyIssues(artifact);
  const openPolicyIssues = policyIssues.filter((issue) => !isResolvedPolicyIssue(issue));
  const citationPack = artifact.citationPackV1 || artifact.pressRelease?.citationPack || null;
  const readiness = artifact.type === 'press_release'
    ? buildReadinessFromArtifact({
        status: artifact.approvalStatus,
        reviewState: artifact.approvalStatus,
        policyIssues,
        citationPackV1: artifact.citationPackV1,
        checkpointId: artifact.checkpointId,
        approvalEnvelopes: artifact.approvalEnvelopes,
        pressRelease: artifact.pressRelease,
        action: 'publish',
      })
    : null;
  const historyCount = Array.isArray(artifact.history) ? artifact.history.length : 0;

  return (
    <aside className={cn('space-y-3', className)} aria-label="Artifact graph companion">
      {run ? (
        <AgentRunCard run={run} showIdentifiers className="mt-0 shadow-none" />
      ) : null}

      <Section title="Draft Preview" icon={<DocumentTextIcon className="h-4 w-4" />}>
        <div className="space-y-2">
          <p className="line-clamp-4 text-sm leading-5 text-foreground">
            {draftPreview || 'No draft text is available yet.'}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <MetricTile label="Version" value={`v${artifact.version || 1}`} />
            <MetricTile label="Status" value={statusLabel(artifact.approvalStatus)} tone={statusTone(artifact.approvalStatus)} />
          </div>
        </div>
      </Section>

      <Section title="Approval Readiness" icon={<ShieldCheckIcon className="h-4 w-4" />}>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <MetricTile
              label="Readiness"
              value={readiness ? statusLabel(readiness.status) : statusLabel(artifact.approvalStatus)}
              tone={statusTone(readiness?.status || artifact.approvalStatus)}
            />
            <MetricTile
              label="Sources"
              value={citationPack ? `${approvedSourceCount(citationPack)}/${citationPack.sourceCards.length}` : String(citations.length)}
            />
          </div>
          {readiness?.blockers.length ? (
            <ul className="space-y-1 text-xs text-muted-foreground">
              {readiness.blockers.slice(0, 3).map((blocker) => (
                <li key={blocker} className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 px-2 py-1.5">
                  <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                  <span className="min-w-0 break-words">{formatApprovalBlocker(blocker)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">
              {artifact.approvalStatus === 'approved'
                ? 'This draft has explicit approval metadata.'
                : 'No publish blockers are visible from current metadata.'}
            </p>
          )}
        </div>
      </Section>

      <Section title="Citations" icon={<BookOpenIcon className="h-4 w-4" />}>
        {citations.length ? (
          <ul className="space-y-2">
            {citations.map((citation) => (
              <li key={citation.id} className="min-w-0 rounded-md border border-border/50 bg-muted/20 px-2 py-2">
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-foreground">{citation.label}</p>
                    {citation.detail ? (
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-muted-foreground">{citation.detail}</p>
                    ) : null}
                  </div>
                  {citation.status ? (
                    <span className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                      {citation.status}
                    </span>
                  ) : null}
                </div>
                {citation.url ? (
                  <a
                    href={citation.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="mt-1 inline-flex max-w-full items-center gap-1 text-[11px] text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <LinkIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">{citation.url}</span>
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No citation metadata is attached to this artifact.</p>
        )}
      </Section>

      <Section title="Policy Blockers" icon={<ExclamationTriangleIcon className="h-4 w-4" />}>
        {openPolicyIssues.length ? (
          <ul className="space-y-1.5">
            {openPolicyIssues.slice(0, 4).map((issue, index) => (
              <li key={`${issue.code || 'policy'}-${index}`} className="rounded-md border border-warning/30 bg-warning/5 px-2 py-1.5 text-xs">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium uppercase tracking-wide text-warning">{normalizeString(issue.code) || 'Policy'}</span>
                  {normalizeString(issue.severity) ? (
                    <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                      {normalizeString(issue.severity)}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 break-words text-muted-foreground">{normalizeString(issue.message) || 'Policy issue requires review.'}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircleIcon className="h-3.5 w-3.5 text-success" />
            No open policy blockers are visible from current metadata.
          </p>
        )}
      </Section>

      <Section title="Version Links" icon={<ClockIcon className="h-4 w-4" />}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{sanitizeArtifactTitle(artifact.title, 'Untitled Artifact')}</p>
            <p className="mt-0.5">
              Current v{artifact.version || 1}
              {historyCount ? ` with ${historyCount} previous ${historyCount === 1 ? 'version' : 'versions'}` : ' with no saved revisions'}
            </p>
            <p className="mt-0.5">Lineage {shortId(artifact.lineageId || artifact.artifactKey || artifact.id)}</p>
          </div>
          <ButtonV2
            variant="secondary"
            size="sm"
            onClick={onOpenHistory}
            disabled={!historyCount || !onOpenHistory}
            className="min-h-10"
          >
            Open history
          </ButtonV2>
        </div>
      </Section>

      {trace ? <DecisionTracePanel trace={trace} defaultOpen={false} /> : null}
      {details ? <RunDetailsPanel details={details} defaultOpen={false} /> : null}
    </aside>
  );
}
